<template>
  <div ref="SFAddToBasketContainerRef">
    <div
      class="flex items-center gap-x-3 transition-all duration-300 max-md:static"
      :class="{
        'max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-100 max-md:bg-white max-md:px-4 max-md:py-2 max-md:shadow-lg max-md:shadow-gray-300 ':
          hasScrolledPastContainer,
      }"
    >
      <SFButton
        data-testid="add-to-basket-button-mobile"
        variant="primary"
        size="xl"
        is-full-width
        :loading="a2bIsLoading"
        :disabled="isSoldOutOrOutOfStock"
        @click="addToBasket"
      >
        <div class="flex w-full items-center justify-center text-sm">
          <template v-if="!isSoldOutOrOutOfStock">
            {{ $t('add_to_basket.add_to_basket') }}
          </template>
          <template v-else>
            {{ $t('global.sold_out') }}
          </template>
          <div class="flex items-center pl-1.5">
            <IconBasket class="size-6" />
          </div>
        </div>
      </SFButton>

      <div class="!size-13">
        <ClientOnly>
          <SFWishlistToggle
            fab
            :product="product"
            :variant="variant"
            class="!size-13 !bg-gray-100 !text-anthracite-800 hover:!scale-100 hover:!bg-gray-200"
          />
          <template #fallback>
            <span class="size-13 rounded-full bg-gray-100" />
          </template>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef } from 'vue'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useIntersectionObserver } from '@vueuse/core'
import { SFButton } from '#storefront-ui/components'
import SFWishlistToggle from '~/components/product/SFWishlistToggle.vue'
import { useDefaultBreakpoints } from '~/modules/ui/runtime'
import { ClientOnly } from '#components'

const {
  product,
  addItemToBasket: addToBasketProp,
  variant,
} = defineProps<{
  product: Product
  isSoldOutOrOutOfStock?: boolean
  addItemToBasket: () => Promise<void>
  variant?: Variant
}>()

const { greater } = useDefaultBreakpoints()
const containerRef = useTemplateRef('SFAddToBasketContainerRef')
const hasScrolledPastContainer = ref(false) // Start as hidden on mobile
const a2bIsLoading = ref(false)
const isDesktop = computed(() => greater('md').value)

const { stop } = useIntersectionObserver(
  containerRef,
  ([entry]) => {
    if (isDesktop.value) {
      hasScrolledPastContainer.value = false
      return
    }
    if (entry.isIntersecting) {
      hasScrolledPastContainer.value = false
    }
    if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
      hasScrolledPastContainer.value = true
    }
  },
  {
    immediate: true,
    rootMargin: '-50px 0px', // -50px from the top due to header height
  },
)

onUnmounted(() => {
  stop()
})

const addToBasket = async () => {
  a2bIsLoading.value = true
  try {
    await addToBasketProp()
  } finally {
    a2bIsLoading.value = false
  }
}
</script>
